import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { IconListSearch } from '@tabler/icons-react'

import { SpinningLoader } from '@/components/interactions/SpinningLoader'
import { fetchAudit } from '@/reducers/kodi/kodi.actions'

import { AuditRecord } from './AuditRecord'

// Mounted by Drawer only while the drawer is open, so its mount effect is the
// "fetch on open" trigger — remounting on reopen refetches. Starts in the
// loading state so the spinner paints on the first frame rather than briefly
// flashing the error state before the request resolves.
export const AuditDrawerContent = ({ auditUuid }) => {
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(true)
  const [record, setRecord] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    const run = async () => {
      try {
        const data = await dispatch(fetchAudit(auditUuid))
        if (!cancelled) {
          setRecord(data)
          setError(null)
        }
      } catch (err) {
        if (!cancelled) {
          setRecord(null)
          setError(err)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [dispatch, auditUuid])

  if (loading) {
    return (
      <div className="kodi-audit__center">
        <SpinningLoader size={22} />
      </div>
    )
  }

  if (error) {
    // 403 (unredacted) / 404 (missing) are permanent for this uuid — surface the
    // backend's reason and drop the retry hint, which can only mislead.
    const permanent = error.status === 403 || error.status === 404
    return (
      <div className="kodi-audit__center kodi-audit__empty">
        <IconListSearch size={26} />
        <p>
          {permanent
            ? error.message
            : 'Couldn’t load the audit trail for this response. Close and reopen to retry.'}
        </p>
      </div>
    )
  }

  if (!record) {
    // Loaded without error but the endpoint returned nothing — a genuine "no
    // audit trail" rather than a failure, so no retry hint.
    return (
      <div className="kodi-audit__center kodi-audit__empty">
        <IconListSearch size={26} />
        <p>No audit trail was recorded for this response.</p>
      </div>
    )
  }

  return <AuditRecord record={record} />
}
